import { IntegrationTypeEnum } from "@jasper/client-models";
import {
  ACTIVATED_ORDER_STATUS,
  UNACTIVATED_ORDER_STATUS,
} from "../types/constants";
import { CommentType, OrderStatus, ProviderOrderStatus } from "../types/enums";
import {
  Order,
  OrderComment,
  OrderHistory,
  Product,
  User,
} from "../types/interfaces";
import { isUserStaff } from "./user.utils";

export const getLastOrderStatusUpdate = (
  orderHistory: OrderHistory[],
  actualOrderStatus: OrderStatus
): { producing: string | null; shipped: string | null } => {
  const sortedOrderHistory = [...orderHistory].sort(
    (a, b) => b.createdAt - a.createdAt
  );
  const producingStatusIndex = getOrderStatusStep(OrderStatus.PRODUCING);
  const shippedStatusIndex = getOrderStatusStep(OrderStatus.SHIPPED);
  const actualOrderIndex = getOrderStatusStep(actualOrderStatus);
  const producingTimestamp = sortedOrderHistory.find(
    history => history.status === OrderStatus.PRODUCING
  )?.createdAt;
  const shippedTimestamp = sortedOrderHistory.find(
    history => history.status === OrderStatus.SHIPPED
  )?.createdAt;
  return {
    producing:
      producingStatusIndex <= actualOrderIndex && producingTimestamp
        ? `le ${new Date(producingTimestamp).toLocaleDateString()}`
        : null,
    shipped:
      shippedStatusIndex <= actualOrderIndex && shippedTimestamp
        ? `le ${new Date(shippedTimestamp).toLocaleDateString()}`
        : null,
  };
};

export const willActivateOrder = ({
  previousOrderStatus,
  nextOrderStatus,
}: {
  previousOrderStatus: OrderStatus;
  nextOrderStatus: OrderStatus;
}): boolean => {
  return (
    UNACTIVATED_ORDER_STATUS.includes(previousOrderStatus) &&
    ACTIVATED_ORDER_STATUS.includes(nextOrderStatus)
  );
};

export const displayStatusName = (status: OrderStatus, user: User) => {
  if (isUserStaff(user)) {
    return status;
  }
  return status === OrderStatus.PRODUCING_IN_TRANSIT
    ? ProviderOrderStatus.SHIPPED_TO_FRANCE
    : status;
};

export const isDayIncludeInBankHolidays = (date, bankHolidays) => {
  for (const i in bankHolidays ?? []) {
    if (date >= bankHolidays[i].beginDate && date <= bankHolidays[i].endDate) {
      return true;
    }
  }
  return false;
};

export const getOrderStatusStep = (status: OrderStatus): number => {
  switch (status) {
    case OrderStatus.DRAFT:
    case OrderStatus.NEED_TO_BE_PROCESSED:
      return 0;
    case OrderStatus.CONFIRMED:
      return 1;
    case OrderStatus.PRODUCING:
    case OrderStatus.WAITING_FOR_PRODUCTION:
    case OrderStatus.ON_HOLD:
    case OrderStatus.PRODUCING_IN_TRANSIT:
    case OrderStatus.OPS_SHIPPING_TODAY:
      return 2;
    case OrderStatus.SHIPPED:
      return 3;
    case OrderStatus.DELIVERED:
      return 4;
  }
  return 0;
};

export const getOrderFirstProduct = (newOrder: any): Product => {
  return newOrder.products[0] ?? null;
};

export const getAllOrderComments = (order: Order): any[] => {
  return (order?.orderComment ?? []).filter(
    comment =>
      comment.type === CommentType.ORDER || comment.type === CommentType.HELP
  );
};

export const getAllPartialDentureComments = (order: Order): OrderComment[] => {
  return (order?.orderComment ?? []).filter(
    comment => comment.type === CommentType.PARTIAL_DENTURE
  );
};

export const getAllRedoComments = (order: Order): any[] => {
  return (order?.orderComment ?? []).filter(
    comment => comment.type === CommentType.REDO
  );
};

export const displayOrderComments = (order: Order): string => {
  const orderComment = getAllOrderComments(order);
  return orderComment.map(comment => comment.comment).join(". ");
};

export const displayPartialDentureComments = (order: Order): string => {
  const partialDentureComment = getAllPartialDentureComments(order);
  return partialDentureComment?.length > 0
    ? partialDentureComment[0]?.comment
    : "";
};

export const convertDateToReadableFormat = (timestamp: number) => {
  if (!timestamp) {
    return "Unplanned yet";
  }
  const dt = new Date(timestamp);
  return `${dt.toDateString()}`;
};

export const getLastShippingDateFromDeliveryDate = (date: Date) => {
  if (!date) {
    return "Unplanned yet";
  }
  const dt = new Date(date);
  const lastShippingDate = new Date(date);
  lastShippingDate.setDate(lastShippingDate.getDate() - 3);
  if (
    lastShippingDate.getDay() === 0 ||
    lastShippingDate.getDay() === 6 ||
    lastShippingDate.getDay() === 5
  ) {
    lastShippingDate.setDate(lastShippingDate.getDate() - 2);
  }
  return `${lastShippingDate.toDateString()}`;
};

export const getDeliveryDateAsString = deliveryDate => {
  if (deliveryDate) {
    const estimatedDeliveryDate = new Date(deliveryDate);
    return estimatedDeliveryDate.toLocaleString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    });
  }
  return null;
};

// Until the ops/support from labs sets the delivery date, the delivery is expected 10 days after the order
const DEFAULT_DELIVERY_DELAY_DAYS = 10;

export const getExpectedDeliveryDate = (
  deliveryDate: Date | number | null | undefined,
  orderDate: Date | string
): Date => {
  if (deliveryDate != null) {
    return new Date(Number(deliveryDate));
  }
  const expectedDeliveryDate = new Date(orderDate);
  expectedDeliveryDate.setDate(
    expectedDeliveryDate.getDate() + DEFAULT_DELIVERY_DELAY_DAYS
  );
  return expectedDeliveryDate;
};

// Link to the order on the vendor's platform, shown under the order reference
// in the order view. 3Shape has no such page. The hosts are the production
// platforms, shared with staging (Shining's debug app runs on the same cloud,
// DS Core's sandbox lives on the EU region host r2 like production).
export const getExternalOrderLink = (
  order: Pick<Order, "directId" | "integrationType">
): { label: string; url: string } | null => {
  if (!order.directId) return null;

  switch (order.integrationType) {
    case IntegrationTypeEnum.MEDITLINK:
      return {
        label: `MeditLink #${order.directId}`,
        url: `https://www.meditlink.com/inbox/detail/${order.directId}`,
      };
    case IntegrationTypeEnum.SHINING3D:
      return {
        label: `Shining3D #${order.directId}`,
        url: `https://ff.dental3dcloud.com/u/case/${order.directId}?type=details`,
      };
    case IntegrationTypeEnum.DSCORE:
      return {
        label: `DS Core #${order.directId}`,
        url: `https://r2.dscore.com/#/orders/${order.directId}`,
      };
    default:
      return null;
  }
};
